import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IInventoryAssigneeShort, ISubdivisionShort, IUserShort } from 'common/models';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  IInventory,
  IAddInventoryAssigneePayload,
  ICreateInventoryAssigneePayload,
  IDeleteInventoryAssigneePayload,
  IRemoveInventoryAssigneePayload,
  IInventoryAssigneesParams,
  ICreateInventoryPayload,
  IInventoryDetailed,
  IInventoryListPayload,
  IInventoryListSubdivisionsParams,
  IInventoryListUsersParams,
  IInventoryParams,
} from 'entities/Inventory/Inventory.models';
import { inventoryTransport } from 'entities/Inventory/Inventory.transport';
import { IUser, IUserListParams } from 'entities/User/User.models';
import { userTransport } from 'entities/User/User.transport';
import { EInventoryActionStatus } from 'entities/Inventory/Inventory.enum';
import { mapInventoryActionToInventoryActionShort } from 'entities/Inventory/Inventory.helper';
import { IInventoryAction } from 'entities/InventoryAction/InventoryAction.models';

interface IState {
  inventory: IInventoryDetailed | null;
  inventoryList: IInventory[];
  inventoryCount: number;
  isInventoryLoading: boolean;
  inventoryListSubdivisions: ISubdivisionShort[];
  inventoryListUsers: IUserShort[];
  inventoryUsers: IUser[];
  inventoryAssignees: IInventoryAssigneeShort[];
}

const initialState: IState = {
  inventory: null,
  inventoryList: [],
  inventoryCount: 0,
  isInventoryLoading: false,
  inventoryListSubdivisions: [],
  inventoryListUsers: [],
  inventoryUsers: [],
  inventoryAssignees: [],
};

export const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setInventory(state, action: PayloadAction<IInventoryDetailed | null>) {
      state.inventory = action.payload;
    },
    setInventoryList(state, action: PayloadAction<IInventory[]>) {
      state.inventoryList = action.payload;
    },
    setInventoryListChunk(state, action: PayloadAction<IInventory[]>) {
      state.inventoryList.push(...action.payload);
    },
    setInventoryCount(state, action: PayloadAction<number>) {
      state.inventoryCount = action.payload;
    },
    setIsInventoryLoading(state, action: PayloadAction<boolean>) {
      state.isInventoryLoading = action.payload;
    },
    setInventoryListSubdivisions(state, action: PayloadAction<ISubdivisionShort[]>) {
      state.inventoryListSubdivisions = action.payload;
    },
    setInventoryListUsers(state, action: PayloadAction<IUserShort[]>) {
      state.inventoryListUsers = action.payload;
    },
    addInventoryListItem(state, action: PayloadAction<IInventory>) {
      const inventory = state.inventoryList.find((item) => item.id === action.payload.id);

      if (inventory) {
        Object.assign(inventory, action.payload);
      } else {
        state.inventoryList.push(action.payload);
      }
    },
    patchInventoryListItem(state, action: PayloadAction<IInventory>) {
      const inventory = state.inventoryList.find((item) => item.id === action.payload.id);

      if (!inventory) {
        return;
      }

      Object.assign(inventory, action.payload);
    },
    addInventoryListItemAction(state, action: PayloadAction<IInventoryAction>) {
      const inventory = state.inventoryList.find((item) => item.id === action.payload.inventoryId);

      if (!inventory) {
        return;
      }

      if (action.payload.status === EInventoryActionStatus.New) {
        inventory.actions.push(mapInventoryActionToInventoryActionShort(action.payload));
      } else {
        inventory.count = inventory.count - action.payload.count;
      }
    },
    filterInventoryListItemActions(state, action: PayloadAction<{ inventoryId: number; id: number }>) {
      const inventory = state.inventoryList.find((item) => item.id === action.payload.inventoryId);

      if (!inventory) {
        return;
      }

      inventory.actions = inventory.actions.filter((item) => item.id !== action.payload.id);
    },
    removeInventoryListItemAssignee(state, action: PayloadAction<number>) {
      state.inventoryList = state.inventoryList.map((item) => {
        return {
          ...item,
          assignees: item.assignees.filter((i) => i.id !== action.payload),
        };
      });
    },
    filterInventoryListByCount(state) {
      const inventoryIds = state.inventoryList.filter((item) => item.count === 0).map((item) => item.id);

      state.inventoryList = state.inventoryList.filter((item) => !inventoryIds.includes(item.id));
      state.inventoryCount = state.inventoryCount - inventoryIds.length;
    },
    setInventoryUsers(state, action: PayloadAction<IUser[]>) {
      state.inventoryUsers = action.payload;
    },
    setInventoryAssignees(state, action: PayloadAction<IInventoryAssigneeShort[]>) {
      state.inventoryAssignees = action.payload;
    },
    addInventoryAssigneesItem(state, action: PayloadAction<IInventoryAssigneeShort>) {
      state.inventoryAssignees.push(action.payload);
    },
    removeInventoryAssigneesItem(state, action: PayloadAction<number>) {
      state.inventoryAssignees = state.inventoryAssignees.filter((item) => item.id !== action.payload);

      if (state.inventory) {
        state.inventory.assignees = state.inventory.assignees.filter((item) => item.id !== action.payload);
      }
    },
    filterInventoryActions(state, action: PayloadAction<number>) {
      if (!state.inventory) {
        return;
      }

      state.inventory.actions = state.inventory.actions.filter((item) => item.id !== action.payload);
    },
  },
});

export const {
  setInventory,
  setInventoryList,
  setInventoryListChunk,
  setInventoryCount,
  setIsInventoryLoading,
  setInventoryListSubdivisions,
  setInventoryListUsers,
  addInventoryListItem,
  patchInventoryListItem,
  addInventoryListItemAction,
  filterInventoryListItemActions,
  removeInventoryListItemAssignee,
  filterInventoryListByCount,
  setInventoryUsers,
  setInventoryAssignees,
  addInventoryAssigneesItem,
  removeInventoryAssigneesItem,
  filterInventoryActions,
} = inventorySlice.actions;

export const selectInventory = (state: RootState) => state.inventory.inventory;
export const selectInventoryList = (state: RootState) => state.inventory.inventoryList;
export const selectInventoryCount = (state: RootState) => state.inventory.inventoryCount;
export const selectIsInventoryLoading = (state: RootState) => state.inventory.isInventoryLoading;
export const selectInventoryListSubdivisions = (state: RootState) => state.inventory.inventoryListSubdivisions;
export const selectInventoryListUsers = (state: RootState) => state.inventory.inventoryListUsers;
export const selectInventoryUsers = (state: RootState) => state.inventory.inventoryUsers;
export const selectInventoryAssignees = (state: RootState) => state.inventory.inventoryAssignees;

export const inventoryReducer = inventorySlice.reducer;

export const getInventoryList = (payload: IInventoryListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.get(payload);
    dispatch(setInventoryList(response.data));
    dispatch(setInventoryCount(response.count));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const getInventoryListChunk = (payload: IInventoryListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.get(payload);
    dispatch(setInventoryListChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const getInventoryListSubdivisions = (params: IInventoryListSubdivisionsParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.getSubdivisions(params);
    dispatch(setInventoryListSubdivisions(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const getInventoryListUsers = (params: IInventoryListUsersParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.getUsers(params);
    dispatch(setInventoryListUsers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const getInventoryById =
  ({ id, onSuccess }: IInventoryParams) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsInventoryLoading(true));

    try {
      const response = await inventoryTransport.getById(id);
      dispatch(setInventory(response));
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error);
    } finally {
      dispatch(setIsInventoryLoading(false));
    }
  };

export const createInventory = (payload: ICreateInventoryPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.create(payload);
    dispatch(addInventoryListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const getInventoryUsers = (params: IUserListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await userTransport.get(params);
    dispatch(setInventoryUsers(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const addInventoryAssignee = (payload: IAddInventoryAssigneePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.addAssignee(payload);
    dispatch(setInventory(response));
    dispatch(patchInventoryListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const removeInventoryAssignee = (payload: IRemoveInventoryAssigneePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.removeAssignee(payload);
    dispatch(setInventory(response));
    dispatch(patchInventoryListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const getInventoryAssignees = (payload: IInventoryAssigneesParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.getAssignees(payload);
    dispatch(setInventoryAssignees(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const createInventoryAssignee = (payload: ICreateInventoryAssigneePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.createAssignee(payload);
    dispatch(addInventoryAssigneesItem(response));
    payload.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};

export const deleteInventoryAssignee = (payload: IDeleteInventoryAssigneePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsInventoryLoading(true));

  try {
    const response = await inventoryTransport.deleteAssignee(payload.id);
    dispatch(removeInventoryAssigneesItem(response.id));
    dispatch(removeInventoryListItemAssignee(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsInventoryLoading(false));
  }
};
